<template>
  <div>
    <!-- Filtro de Período -->
    <div class="mb-6">
      <PeriodFilter v-model="filters" @change="handleFilterChange" />
    </div>

    <!-- Cards de Indicadores -->
    <div v-if="loading" class="text-center py-8">
      <vs-button type="border" class="mb-2" :disabled="true">
        <span class="animate-spin">Carregando...</span>
      </vs-button>
    </div>

    <template v-else>
      <!-- Cards de Indicadores -->
      <div
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6"
      >
        <DashboardStatisticsCard
          title="Total de Alunos"
          :value="dashboardData.total_students"
          description="Alunos cadastrados"
          type="primary"
          icon="UsersIcon"
          icon-color="success"
          value-size="large"
          :formatter="formatNumber"
        />

        <DashboardStatisticsCard
          title="Alunos no Período"
          :value="dashboardData.students_in_period"
          description="Cadastrados no período selecionado"
          type="info"
          icon="CalendarIcon"
          value-size="small"
          :formatter="formatNumber"
        />

        <DashboardStatisticsCard
          title="Alunos Este Mês"
          :value="dashboardData.students_this_month"
          description="Cadastrados este mês"
          type="success"
          icon="TrendingUpIcon"
          value-size="small"
          :formatter="formatNumber"
        />

        <DashboardStatisticsCard
          title="Taxa de Crescimento"
          :value="dashboardData.growth_rate"
          description="Comparado ao mês anterior"
          type="warning"
          icon="TrendingUpIcon"
          value-size="small"
          :formatter="formatGrowthRate"
        />

        <DashboardStatisticsCard
          title="Alunos Ativos"
          :value="dashboardData.active_students"
          description="Com aulas agendadas"
          type="success"
          icon="CheckCircleIcon"
          value-size="large"
          :formatter="formatNumber"
        />

        <DashboardStatisticsCard
          title="Alunos Inativos"
          :value="dashboardData.inactive_students"
          description="Sem aulas agendadas"
          type="warning"
          icon="UserXIcon"
          value-size="large"
          :formatter="formatNumber"
        />
      </div>

      <!-- Gráficos -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <!-- Gráfico de Linha - Evolução de Cadastros -->
        <vx-card>
          <template slot="no-body">
            <div class="p-6">
              <h3 class="mb-4 font-semibold text-lg">
                Evolução de Cadastros
              </h3>
              <vue-apex-charts
                v-if="
                  dashboardData.evolution_data &&
                  dashboardData.evolution_data.labels &&
                  dashboardData.evolution_data.labels.length > 0
                "
                type="line"
                height="350"
                :options="evolutionChartOptions"
                :series="evolutionSeries"
              />
              <div
                v-else
                class="flex items-center justify-center h-64 text-gray-500"
              >
                Nenhum dado disponível
              </div>
            </div>
          </template>
        </vx-card>

        <!-- Gráfico de Pizza - Distribuição por Modalidade -->
        <vx-card>
          <template slot="no-body">
            <div class="p-6">
              <h3 class="mb-4 font-semibold text-lg">
                Distribuição por Modalidade
              </h3>
              <vue-apex-charts
                v-if="
                  modalitySeries &&
                  modalitySeries.length > 0 &&
                  modalityLabels.length > 0
                "
                type="donut"
                height="350"
                :options="modalityChartOptions"
                :series="modalitySeries"
              />
              <div
                v-else
                class="flex items-center justify-center h-64 text-gray-500"
              >
                Nenhum dado disponível
              </div>
            </div>
          </template>
        </vx-card>
      </div>
    </template>
  </div>
</template>

<script>
import PeriodFilter from '@/components/filters/PeriodFilter.vue'
import DashboardStatisticsCard from '@/components/statistics-cards/DashboardStatisticsCard.vue'
import StudentDashboardService from '@/services/api/StudentDashboardService'
import VueApexCharts from 'vue-apexcharts'

export default {
  components: {
    PeriodFilter,
    DashboardStatisticsCard,
    VueApexCharts
  },
  data() {
    return {
      service: null,
      filters: {
        start_date: null,
        end_date: null
      },
      dashboardData: {
        total_students: 0,
        students_in_period: 0,
        students_this_month: 0,
        students_last_month: 0,
        growth_rate: 0,
        active_students: 0,
        inactive_students: 0,
        evolution_data: {
          labels: [],
          data: []
        },
        modality_distribution: []
      },
      loading: false
    }
  },
  computed: {
    evolutionSeries() {
      return [
        {
          name: 'Cadastros',
          data: this.dashboardData.evolution_data?.data || []
        }
      ]
    },
    evolutionChartOptions() {
      return {
        chart: {
          type: 'line',
          toolbar: {
            show: false
          }
        },
        stroke: {
          curve: 'smooth',
          width: 3
        },
        colors: ['#7367f0'],
        xaxis: {
          categories: this.dashboardData.evolution_data?.labels || [],
          labels: {
            style: {
              fontSize: '12px'
            }
          }
        },
        yaxis: {
          labels: {
            formatter: val => {
              return Math.round(val).toString()
            },
            style: {
              fontSize: '12px'
            }
          }
        },
        tooltip: {
          y: {
            formatter: val => {
              return `${val} aluno${val !== 1 ? 's' : ''}`
            }
          }
        },
        grid: {
          borderColor: '#e7e7e7',
          strokeDashArray: 4
        },
        markers: {
          size: 5,
          hover: {
            size: 7
          }
        }
      }
    },
    modalitySeries() {
      return this.dashboardData.modality_distribution?.map(
        item => item.count
      ) || []
    },
    modalityLabels() {
      return this.dashboardData.modality_distribution?.map(
        item => item.name
      ) || []
    },
    modalityChartOptions() {
      const total = this.modalitySeries.reduce((a, b) => a + b, 0)

      return {
        chart: {
          type: 'donut',
          toolbar: {
            show: false
          }
        },
        labels: this.modalityLabels,
        colors: [
          '#7367f0',
          '#28c76f',
          '#00cfe8',
          '#ff9f43',
          '#ea5455',
          '#82868b'
        ],
        legend: {
          position: 'bottom',
          fontSize: '14px',
          formatter: (seriesName, opts) => {
            const value = opts.w.globals.series[opts.seriesIndex] || 0
            const percentage =
              total > 0 ? ((value / total) * 100).toFixed(1) : '0.0'
            return `${seriesName}: ${value} (${percentage}%)`
          }
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: 200
              },
              legend: {
                position: 'bottom'
              }
            }
          }
        ],
        dataLabels: {
          enabled: true,
          formatter: (val, opts) => {
            return Number(val).toFixed(1) + '%'
          },
          style: {
            fontSize: '14px',
            fontWeight: 'bold'
          }
        },
        tooltip: {
          y: {
            formatter: val => {
              const percentage =
                total > 0 ? ((val / total) * 100).toFixed(1) : '0.0'
              return `${val} aluno${val !== 1 ? 's' : ''} (${percentage}%)`
            }
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total',
                  formatter: () => {
                    return total.toString()
                  },
                  fontSize: '16px',
                  fontWeight: 'bold'
                }
              }
            }
          }
        }
      }
    }
  },
  methods: {
    formatNumber(value) {
      return new Intl.NumberFormat('pt-BR').format(value || 0)
    },
    formatGrowthRate(value) {
      const rate = value || 0
      const sign = rate >= 0 ? '+' : ''
      return `${sign}${rate.toFixed(2)}%`
    },
    handleFilterChange(filters) {
      this.filters = { ...filters }
      this.loadDashboard()
    },
    async loadDashboard() {
      this.loading = true
      try {
        const customFilters = {}
        if (this.filters.start_date) {
          customFilters.start_date = this.filters.start_date
        }
        if (this.filters.end_date) {
          customFilters.end_date = this.filters.end_date
        }

        const data = await this.service.getDashboard(customFilters)
        this.dashboardData = {
          ...this.dashboardData,
          ...data
        }
      } catch (error) {
        console.error('Error loading dashboard:', error)
        this.$vs.notify({
          title: 'Erro',
          text: 'Erro ao carregar dados da dashboard',
          color: 'danger'
        })
      } finally {
        this.loading = false
      }
    }
  },
  mounted() {
    this.service = StudentDashboardService.build(this.$vs)
    this.loadDashboard()
  }
}
</script>


